import { validateDecimal, validateHexColor, validateInteger, validateUrlAllowedSchemes } from './validators';

type ServerCustomField = {
  meta_field_type?: string;
  valid_unit_values?: string[];
};

export type CustomFieldFieldConfig = {
  type: string;
  value_in_object: boolean;
  options?: string[];
  defaultUnit?: string;
  inputProps?: Record<string, any>;
  validate?: (value: unknown) => string | undefined;
  tip?: { type: 'info' | 'warning' | 'error' | 'success'; description: string };
};

export const getCustomFieldFieldConfigFromServer = (obj: ServerCustomField): CustomFieldFieldConfig => {
  const config: CustomFieldFieldConfig = {
    type: 'text',
    value_in_object: false,
  };

  switch (obj?.meta_field_type) {
    case 'dimension':
      config.type = 'dimension';
      config.value_in_object = true;
      config.options = obj.valid_unit_values;
      config.validate = validateDecimal;
      break;
    case 'volume':
      config.type = 'volume';
      config.value_in_object = true;
      config.options = obj.valid_unit_values;
      config.validate = validateDecimal;
      break;
    case 'weight':
      config.type = 'weight';
      config.value_in_object = true;
      config.options = obj.valid_unit_values;
      config.defaultUnit = 'lb';
      config.validate = validateDecimal;
      break;
    case 'rating':
      config.type = 'rating';
      config.value_in_object = true;
      config.options = obj.valid_unit_values;
      break;
    case 'boolean':
      config.type = 'boolean';
      config.value_in_object = false;
      config.options = obj.valid_unit_values;
      break;
    case 'number_integer':
      config.type = 'number';
      config.value_in_object = false;
      config.inputProps = { step: 1 };
      config.validate = validateInteger;
      break;
    case 'number_decimal':
      config.type = 'number';
      config.value_in_object = false;
      config.inputProps = { step: 'any' };
      config.validate = validateDecimal;
      break;
    case 'date':
      config.type = 'date';
      config.value_in_object = false;
      break;
    case 'date_time':
      config.type = 'datetime';
      config.value_in_object = false;
      break;
    case 'url':
      config.type = 'url';
      config.value_in_object = false;
      config.validate = validateUrlAllowedSchemes;
      break;
    case 'color':
      config.type = 'color';
      config.value_in_object = false;
      config.validate = validateHexColor;
      break;
    default:
      break;
  }

  return config;
};

export const coerceCustomFieldBooleanValue = (value: unknown): boolean => {
  if (value == null) {
    return false;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return false;
    }
    return value.some((entry) => coerceCustomFieldBooleanValue(entry));
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['true', '1', 'on', 'yes'].includes(normalized);
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return false;
};

export const normalizeCustomFieldBooleans = (fields: Record<string, unknown> | undefined, booleanFieldNames: string[]): Record<string, unknown> => {
  const nextFields: Record<string, unknown> = { ...(fields ?? {}) };
  booleanFieldNames.forEach((fieldName) => {
    nextFields[fieldName] = coerceCustomFieldBooleanValue(nextFields[fieldName]);
  });
  return nextFields;
};
